import { useState, useEffect } from "react";
import { Activity, X, AlertTriangle, TrendingUp, Calendar, MapPin, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis
} from "recharts";
import { createPortal } from "react-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getMagnitudeColor = (mag) => {
  if (mag >= 5.0) return "#ef4444"; // red
  if (mag >= 4.0) return "#f97316"; // orange
  if (mag >= 3.0) return "#eab308"; // yellow
  if (mag >= 2.0) return "#22c55e"; // green
  return "#6b7280"; // gray
};

const getMagnitudeLabel = (mag) => {
  if (mag >= 5.0) return "Fuerte";
  if (mag >= 4.0) return "Moderado";
  if (mag >= 3.0) return "Menor";
  if (mag >= 2.0) return "Micro";
  return "Muy débil";
};

const SeismicHistory = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const doFetch = async () => {
      if (!isOpen) return;
      
      console.log('=== Starting fetchHistory ===');
      setLoading(true);
      setError(null);
      
      const url = `${API_URL}/api/seismic/history?days=${period}`;
      console.log('URL:', url);
      
      try {
        const response = await fetch(url);
        console.log('Response received, status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const result = await response.json();
        console.log('Data parsed, success:', result.success, 'total:', result.total_count);
        
        if (isMounted) {
          if (result.success) {
            setData(result);
            console.log('Data set successfully');
          } else {
            setError(result.error || "Error en respuesta del servidor");
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Fetch error:', err.name, err.message);
        if (isMounted) {
          setError("Error de conexión: " + err.message);
          setLoading(false);
        }
      }
    };
    
    doFetch();
    
    return () => {
      isMounted = false;
    };
  }, [isOpen, period]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm" data-testid="seismic-history-modal">
      <div className="bg-[#0a0f18] border border-orange-900/50 rounded-xl w-[96vw] max-w-6xl max-h-[95vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-orange-900/30 bg-gradient-to-r from-orange-900/40 to-red-900/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-600/30 rounded-lg">
              <Activity className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white uppercase tracking-wide">Histórico Sísmico</h2>
              <p className="text-xs text-zinc-400">
                Radio 500 km desde La Aldea · Fuente: EMSC
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Period selector */}
            <div className="flex gap-1">
              {[7, 30, 90, 365].map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={period === d ? "default" : "outline"}
                  onClick={() => setPeriod(d)}
                  className={period === d 
                    ? "bg-orange-600 text-white" 
                    : "bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700"
                  }
                >
                  {d === 365 ? "1 año" : `${d} días`}
                </Button>
              ))}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(95vh-70px)]">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
              <span className="ml-3 text-zinc-400">Cargando datos sísmicos...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-red-400">
              <AlertTriangle className="w-6 h-6 mr-2" />
              {error}
            </div>
          ) : data ? (
            <>
              {/* Statistics Cards */}
              <div className="grid grid-cols-5 gap-3 mb-4">
                <Card className="bg-zinc-900/50 border-zinc-800 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4 text-orange-400" />
                    <span className="text-[10px] text-zinc-400 uppercase">Total Sismos</span>
                  </div>
                  <p className="text-2xl font-bold text-white font-mono">{data.statistics?.total || 0}</p>
                  <p className="text-[10px] text-zinc-500">últimos {period} días</p>
                </Card>
                
                <Card className="bg-zinc-900/50 border-zinc-800 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-red-400" />
                    <span className="text-[10px] text-zinc-400 uppercase">Magnitud Máx</span>
                  </div>
                  <p className="text-2xl font-bold text-red-400 font-mono">{data.statistics?.max_magnitude || 0}</p>
                  <p className="text-[10px] text-zinc-500">escala Richter</p>
                </Card>
                
                <Card className="bg-zinc-900/50 border-zinc-800 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4 text-yellow-400" />
                    <span className="text-[10px] text-zinc-400 uppercase">Magnitud Media</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-400 font-mono">{data.statistics?.average_magnitude || 0}</p>
                  <p className="text-[10px] text-zinc-500">promedio</p>
                </Card>
                
                <Card className="bg-zinc-900/50 border-zinc-800 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    <span className="text-[10px] text-zinc-400 uppercase">Sismos/Día</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-400 font-mono">{data.statistics?.daily_average || 0}</p>
                  <p className="text-[10px] text-zinc-500">media diaria</p>
                </Card>
                
                <Card className="bg-zinc-900/50 border-zinc-800 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-green-400" />
                    <span className="text-[10px] text-zinc-400 uppercase">Radio</span>
                  </div>
                  <p className="text-2xl font-bold text-green-400 font-mono">{data.radius_km || 150}</p>
                  <p className="text-[10px] text-zinc-500">kilómetros</p>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Daily Chart */}
                <Card className="bg-zinc-900/50 border-zinc-800 p-4">
                  <h3 className="text-sm font-bold text-orange-300 uppercase mb-3">Actividad Diaria</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.daily_data || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#888', fontSize: 9 }} 
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis tick={{ fill: '#888', fontSize: 10 }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #f97316', borderRadius: '8px' }}
                        formatter={(value, name) => {
                          if (name === 'count') return [value, 'Sismos'];
                          if (name === 'max_magnitude') return [value, 'Magnitud máx'];
                          return [value, name];
                        }}
                      />
                      <Bar dataKey="count" name="count" radius={[4, 4, 0, 0]}>
                        {(data.daily_data || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getMagnitudeColor(entry.max_magnitude)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                {/* Category Distribution */}
                <Card className="bg-zinc-900/50 border-zinc-800 p-4">
                  <h3 className="text-sm font-bold text-orange-300 uppercase mb-3">Distribución por Magnitud</h3>
                  <div className="space-y-3">
                    {[
                      { key: 'strong', label: 'Fuertes (≥5.0)', color: '#ef4444' },
                      { key: 'moderate', label: 'Moderados (4.0-4.9)', color: '#f97316' },
                      { key: 'minor', label: 'Menores (3.0-3.9)', color: '#eab308' },
                      { key: 'micro', label: 'Micro (1.5-2.9)', color: '#22c55e' }
                    ].map(({ key, label, color }) => {
                      const count = data.statistics?.by_category?.[key] || 0;
                      const total = data.statistics?.total || 1;
                      const pct = ((count / total) * 100).toFixed(1);
                      return (
                        <div key={key}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-zinc-400">{label}</span>
                            <span className="text-white font-mono">{count} ({pct}%)</span>
                          </div>
                          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${pct}%`, backgroundColor: color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>

              {/* Earthquake List */}
              <Card className="bg-zinc-900/50 border-zinc-800 p-4">
                <h3 className="text-sm font-bold text-orange-300 uppercase mb-3">
                  Últimos Sismos ({Math.min(data.earthquakes?.length || 0, 50)} de {data.total_count})
                </h3>
                <div className="max-h-[250px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-zinc-900">
                      <tr className="text-zinc-400 border-b border-zinc-800">
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-left p-2">Hora</th>
                        <th className="text-center p-2">Magnitud</th>
                        <th className="text-center p-2">Prof. (km)</th>
                        <th className="text-center p-2">Dist. (km)</th>
                        <th className="text-left p-2">Ubicación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.earthquakes || []).slice(0, 50).map((eq, idx) => (
                        <tr key={eq.id || idx} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="p-2 text-zinc-300 font-mono">{eq.date}</td>
                          <td className="p-2 text-zinc-400 font-mono">{eq.time}</td>
                          <td className="p-2 text-center">
                            <span 
                              className="inline-block px-2 py-0.5 rounded font-bold font-mono"
                              style={{ 
                                backgroundColor: `${getMagnitudeColor(eq.magnitude)}20`,
                                color: getMagnitudeColor(eq.magnitude)
                              }}
                            >
                              {eq.magnitude}
                            </span>
                          </td>
                          <td className="p-2 text-center text-zinc-400 font-mono">{eq.depth_km}</td>
                          <td className="p-2 text-center text-zinc-400 font-mono">{eq.distance_km}</td>
                          <td className="p-2 text-zinc-300 truncate max-w-[200px]" title={eq.location}>
                            {eq.location}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!data.earthquakes || data.earthquakes.length === 0) && (
                    <div className="text-center py-8 text-zinc-500">
                      No se registraron sismos en este período
                    </div>
                  )}
                </div>
              </Card>

              <div className="mt-4 p-3 bg-orange-900/10 border border-orange-800/30 rounded-lg">
                <p className="text-xs text-zinc-400">
                  <strong className="text-orange-400">Datos sísmicos:</strong> Proporcionados por el EMSC (European-Mediterranean Seismological Centre).
                  Radio de búsqueda: {data.radius_km || 500} km desde La Aldea de San Nicolás.
                  <span className="text-zinc-500 ml-2">Solo se muestran sismos con magnitud ≥ 1.0</span>
                </p>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Button for header
export const SeismicHistoryButton = ({ onClick }) => {
  return (
    <Button 
      variant="outline" 
      size="sm"
      onClick={onClick}
      className="bg-orange-600/20 border-orange-500/30 text-orange-400 hover:bg-orange-600/30 hover:text-orange-300 h-8 px-3 text-xs"
      data-testid="seismic-history-btn"
    >
      <Activity className="w-4 h-4 mr-1.5" />
      Histórico Sismos
    </Button>
  );
};

export default SeismicHistory;
